"use client";

import type {
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  Ref,
  WheelEvent as ReactWheelEvent,
} from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";

import { cn } from "@/utils/lib";

type HorizontalScrollRailProps = {
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  scrollStep?: number;
  showButtons?: boolean;
  showShadows?: boolean;
  buttonClassName?: string;
  shadowClassName?: string;
  viewportRef?: Ref<HTMLDivElement>;
};

const DRAG_ACTIVATION_DISTANCE_PX = 4;
const WHEEL_LINE_HEIGHT_PX = 16;
const WHEEL_DELTA_LINE_MODE = 1;
const WHEEL_DELTA_PAGE_MODE = 2;

const getMaxScrollLeft = (element: HTMLDivElement) => {
  return Math.max(element.scrollWidth - element.clientWidth, 0);
};

const clampScrollLeft = (element: HTMLDivElement, scrollLeft: number) => {
  return Math.min(Math.max(scrollLeft, 0), getMaxScrollLeft(element));
};

const getWheelScrollDelta = (
  event: ReactWheelEvent<HTMLDivElement>,
  element: HTMLDivElement,
) => {
  const dominantDelta =
    Math.abs(event.deltaX) > Math.abs(event.deltaY)
      ? event.deltaX
      : event.deltaY;

  if (event.deltaMode === WHEEL_DELTA_LINE_MODE) {
    return dominantDelta * WHEEL_LINE_HEIGHT_PX;
  }

  if (event.deltaMode === WHEEL_DELTA_PAGE_MODE) {
    return dominantDelta * element.clientWidth;
  }

  return dominantDelta;
};

type DragState = {
  pointerId: number;
  startScrollLeft: number;
  startX: number;
  dragged: boolean;
};

export const HorizontalScrollRail = ({
  children,
  className,
  contentClassName,
  scrollStep = 240,
  showButtons = true,
  showShadows = true,
  buttonClassName,
  shadowClassName,
  viewportRef,
}: HorizontalScrollRailProps) => {
  const railRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const suppressClickRef = useRef(false);
  const suppressClickTimeoutRef = useRef<number | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const updateScrollState = useCallback(() => {
    const element = railRef.current;

    if (!element) {
      return;
    }

    const { scrollLeft } = element;
    const maxScrollLeft = getMaxScrollLeft(element);

    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft < maxScrollLeft - 1);
  }, []);

  const setViewportRef = useCallback(
    (element: HTMLDivElement | null) => {
      railRef.current = element;

      if (!viewportRef) {
        return;
      }

      if (typeof viewportRef === "function") {
        viewportRef(element);
        return;
      }

      viewportRef.current = element;
    },
    [viewportRef],
  );

  useEffect(() => {
    const element = railRef.current;

    if (!element) {
      return;
    }

    updateScrollState();

    element.addEventListener("scroll", updateScrollState, {
      passive: true,
    });

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(element);

    return () => {
      element.removeEventListener("scroll", updateScrollState);
      resizeObserver.disconnect();
    };
  }, [updateScrollState]);

  useEffect(() => {
    const rafId = window.requestAnimationFrame(updateScrollState);

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [children, updateScrollState]);

  useEffect(() => {
    return () => {
      if (suppressClickTimeoutRef.current !== null) {
        window.clearTimeout(suppressClickTimeoutRef.current);
      }
    };
  }, []);

  const suppressNextClick = useCallback(() => {
    suppressClickRef.current = true;

    if (suppressClickTimeoutRef.current !== null) {
      window.clearTimeout(suppressClickTimeoutRef.current);
    }

    suppressClickTimeoutRef.current = window.setTimeout(() => {
      suppressClickRef.current = false;
      suppressClickTimeoutRef.current = null;
    }, 0);
  }, []);

  const stopDragging = useCallback(
    (element: HTMLDivElement, pointerId: number) => {
      const dragState = dragStateRef.current;

      if (!dragState || dragState.pointerId !== pointerId) {
        return;
      }

      if (
        typeof element.hasPointerCapture === "function" &&
        element.hasPointerCapture(pointerId)
      ) {
        element.releasePointerCapture(pointerId);
      }

      if (dragState.dragged) {
        suppressNextClick();
      }

      dragStateRef.current = null;
      setIsDragging(false);
    },
    [suppressNextClick],
  );

  const scrollByAmount = (direction: "left" | "right") => {
    const element = railRef.current;

    if (!element) {
      return;
    }

    element.scrollBy({
      left: direction === "left" ? -scrollStep : scrollStep,
      behavior: "smooth",
    });
  };

  const handleWheel = useCallback(
    (event: ReactWheelEvent<HTMLDivElement>) => {
      const element = event.currentTarget;

      if (getMaxScrollLeft(element) <= 0) {
        return;
      }

      const delta = getWheelScrollDelta(event, element);

      if (delta === 0) {
        return;
      }

      const nextScrollLeft = clampScrollLeft(
        element,
        element.scrollLeft + delta,
      );

      if (Math.abs(nextScrollLeft - element.scrollLeft) < 0.5) {
        return;
      }

      event.preventDefault();
      element.scrollLeft = nextScrollLeft;
      updateScrollState();
    },
    [updateScrollState],
  );

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (
        event.button !== 0 ||
        event.pointerType !== "mouse" ||
        getMaxScrollLeft(event.currentTarget) <= 0
      ) {
        return;
      }

      dragStateRef.current = {
        pointerId: event.pointerId,
        startScrollLeft: event.currentTarget.scrollLeft,
        startX: event.clientX,
        dragged: false,
      };
    },
    [],
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const dragState = dragStateRef.current;

      if (!dragState || dragState.pointerId !== event.pointerId) {
        return;
      }

      const dragDistance = event.clientX - dragState.startX;

      if (!dragState.dragged) {
        if (Math.abs(dragDistance) < DRAG_ACTIVATION_DISTANCE_PX) {
          return;
        }

        dragState.dragged = true;
        event.currentTarget.setPointerCapture?.(event.pointerId);
        setIsDragging(true);
      }

      event.preventDefault();

      event.currentTarget.scrollLeft = clampScrollLeft(
        event.currentTarget,
        dragState.startScrollLeft - dragDistance,
      );
      updateScrollState();
    },
    [updateScrollState],
  );

  const handlePointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      stopDragging(event.currentTarget, event.pointerId);
    },
    [stopDragging],
  );

  const handleClickCapture = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!suppressClickRef.current) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      suppressClickRef.current = false;
    },
    [],
  );

  return (
    <div className={cn("relative min-w-0", className)}>
      {showShadows && canScrollLeft && (
        <div
          className={cn(
            `
              pointer-events-none absolute inset-y-0 left-0 z-10 w-8
              bg-linear-to-r from-background via-background/80 to-transparent
            `,
            shadowClassName,
          )}
        />
      )}

      {showShadows && canScrollRight && (
        <div
          className={cn(
            `
              pointer-events-none absolute inset-y-0 right-0 z-10 w-8
              bg-linear-to-l from-background via-background/80 to-transparent
            `,
            shadowClassName,
          )}
        />
      )}

      {showButtons && canScrollLeft && (
        <button
          type="button"
          data-horizontal-scroll-rail-button="left"
          className={cn(
            `
              absolute top-1/2 left-0 z-20 inline-flex size-8 -translate-y-1/2
              items-center justify-center text-muted-foreground transition-colors
              hover:text-foreground
            `,
            buttonClassName,
          )}
          onClick={() => scrollByAmount("left")}
        >
          <LuChevronLeft className="size-4" />
          <span className="sr-only">Scroll left</span>
        </button>
      )}

      {showButtons && canScrollRight && (
        <button
          type="button"
          data-horizontal-scroll-rail-button="right"
          className={cn(
            `
              absolute top-1/2 right-0 z-20 inline-flex size-8 -translate-y-1/2
              items-center justify-center text-muted-foreground transition-colors
              hover:text-foreground
            `,
            buttonClassName,
          )}
          onClick={() => scrollByAmount("right")}
        >
          <LuChevronRight className="size-4" />
          <span className="sr-only">Scroll right</span>
        </button>
      )}

      <div
        ref={setViewportRef}
        data-horizontal-scroll-rail-viewport
        onClickCapture={handleClickCapture}
        onDragStart={(event) => event.preventDefault()}
        onPointerCancel={handlePointerUp}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onWheel={handleWheel}
        className={cn(
          `
            no-scrollbar flex min-w-0 items-center gap-2 overflow-x-auto
            overflow-y-hidden p-1
          `,
          isDragging && "cursor-grabbing select-none",
          contentClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
};
